import { useEffect, useRef, useState } from 'react'
import { getDeviceInfo, type Platform } from '@/lib/deviceInfo'
import {
  evaluateVersionCheck,
  subscribeToVersioning,
  withRuntimeWebVersion,
  type VersionCheckEvaluation,
  type VersionNotificationType,
} from '@/lib/versionCheck'

export interface VersionCheckDialogInfo {
  notificationType: VersionNotificationType
  isDismissable: boolean
  platform: Platform
  isHardNative: boolean
}

interface UseVersionCheckOptions {
  /** Called when Firestore versioning indicates the user should update (soft or hard). */
  onUpdateNeeded?: () => void
}

/**
 * Listens to `dynamicData/versioning` via Firestore onSnapshot and evaluates whether to prompt.
 */
export function useVersionCheck(options?: UseVersionCheckOptions) {
  const onUpdateNeededRef = useRef(options?.onUpdateNeeded)
  onUpdateNeededRef.current = options?.onUpdateNeeded

  const [evaluation, setEvaluation] = useState<VersionCheckEvaluation | null>(null)
  const [dialogInfo, setDialogInfo] = useState<VersionCheckDialogInfo | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    const handleLatestVersions = async (
      latestVersions: Parameters<typeof evaluateVersionCheck>[1]
    ) => {
      try {
        const deviceInfo = await withRuntimeWebVersion(await getDeviceInfo())
        if (cancelled) return

        const result = evaluateVersionCheck(deviceInfo, latestVersions)
        setEvaluation(result)
        setDialogInfo({
          notificationType: result.notificationType,
          isDismissable: result.isDismissable,
          platform: deviceInfo.platform,
          isHardNative: result.isHardNative,
        })
        setLoading(false)

        if (result.shouldShow) {
          onUpdateNeededRef.current?.()
        }
      } catch (error) {
        console.error('[VersionCheck] Error evaluating versions:', error)
        if (!cancelled) setLoading(false)
      }
    }

    console.log('[VersionCheck] Subscribing to dynamicData/versioning snapshot...')
    const unsubscribe = subscribeToVersioning(
      (latest) => {
        void handleLatestVersions(latest)
      },
      () => {
        if (!cancelled) setLoading(false)
      }
    )

    return () => {
      cancelled = true
      unsubscribe()
    }
  }, [])

  return {
    loading,
    evaluation,
    dialogInfo,
  }
}
